import { API_BASE_URL } from '../config/api';

/**
 * API Token Service
 * Handles generation and caching of API tokens for protected endpoints
 */
class APITokenService {
  constructor() {
    this.token = null;
    this.tokenExpiry = null;
    this.tokenKey = 'api_token';
    this.expiryKey = 'api_token_expiry';
    
    // Load token from localStorage on initialization
    this.loadTokenFromStorage();
  }

  /**
   * Load token from localStorage
   */
  loadTokenFromStorage() {
    try {
      const storedToken = localStorage.getItem(this.tokenKey);
      const storedExpiry = localStorage.getItem(this.expiryKey);
      
      if (storedToken && storedExpiry) {
        const expiryTime = parseInt(storedExpiry, 10);
        // Check if token is still valid (with 5 minute buffer)
        if (Date.now() < expiryTime - (5 * 60 * 1000)) {
          this.token = storedToken;
          this.tokenExpiry = expiryTime;
          console.log('✅ API Token loaded from storage');
          return true;
        } else {
          console.log('⚠️ Stored token expired, will fetch new one');
          this.clearToken();
        }
      }
    } catch (error) {
      console.error('Error loading token from storage:', error);
    }
    return false;
  }

  /**
   * Save token to localStorage
   */
  saveTokenToStorage(token, expiresIn) {
    try {
      const expiryTime = Date.now() + (expiresIn * 1000);
      localStorage.setItem(this.tokenKey, token);
      localStorage.setItem(this.expiryKey, expiryTime.toString());
      this.token = token;
      this.tokenExpiry = expiryTime;
      console.log('💾 API Token saved to storage, expires in', expiresIn, 'seconds');
    } catch (error) {
      console.error('Error saving token to storage:', error);
    }
  }

  /**
   * Clear token from memory and storage
   */
  clearToken() {
    this.token = null;
    this.tokenExpiry = null;
    try {
      localStorage.removeItem(this.tokenKey);
      localStorage.removeItem(this.expiryKey);
    } catch (error) {
      console.error('Error clearing token:', error);
    }
  }

  /**
   * Check if current token is valid
   */
  isTokenValid() {
    if (!this.token || !this.tokenExpiry) {
      return false;
    }
    // Consider token invalid 5 minutes before actual expiry
    return Date.now() < (this.tokenExpiry - (5 * 60 * 1000));
  }

  /**
   * Fetch a new token from the API
   */
  async fetchNewToken() {
    try {
      console.log('🔑 Fetching new API token...');
      const response = await fetch(`${API_BASE_URL}/api/api_token.php`, {
        method: 'GET',
        mode: 'cors',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch token: ${response.status}`);
      }

      const data = await response.json();
      
      if (data.success && data.token) {
        this.saveTokenToStorage(data.token, data.expires_in || 3600);
        console.log('✅ New API token obtained successfully');
        return data.token;
      } else {
        throw new Error('Invalid token response');
      }
    } catch (error) {
      console.error('❌ Error fetching API token:', error);
      throw error;
    }
  }

  /**
   * Get a valid token (fetch new one if needed)
   */
  async getToken() {
    // If we have a valid token, return it
    if (this.isTokenValid()) {
      console.log('✅ Using cached API token');
      return this.token;
    }

    // Otherwise, fetch a new token
    console.log('⚠️ Token invalid or expired, fetching new token...');
    return await this.fetchNewToken();
  }

  /**
   * Get headers with API token for fetch requests
   */
  async getHeaders(additionalHeaders = {}) {
    const token = await this.getToken();
    return {
      'Content-Type': 'application/json',
      'X-API-Token': token,
      ...additionalHeaders
    };
  }

  /**
   * Make an authenticated API request
   */
  async fetch(url, options = {}) {
    try {
      const headers = await this.getHeaders(options.headers || {});
      
      const response = await fetch(url, {
        mode: 'cors',
        ...options,
        headers
      });

      // If we get 403, token might be invalid - try once more with fresh token
      if (response.status === 403) {
        console.log('⚠️ Got 403, fetching fresh token and retrying...');
        this.clearToken();
        const newHeaders = await this.getHeaders(options.headers || {});
        
        return await fetch(url, {
          mode: 'cors',
          ...options,
          headers: newHeaders
        });
      }

      return response;
    } catch (error) {
      console.error('Error in authenticated fetch:', error);
      throw error;
    }
  }
}

// Create and export a singleton instance
const apiTokenService = new APITokenService();

export default apiTokenService;
